import Button from '@jetbrains/ring-ui/components/button/button'
import {useCallback, useContext, useMemo, useState} from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import type {BuildType} from '../../../../../services/rest'
import type {BuildTypeId, NormalizedBuild} from '../../../../../types'
import {stringifyId} from '../../../../../types'
import {emptyArray} from '../../../../../utils/empty'
import type {KeyValue} from '../../../../../utils/object'

import {ChangeBuildsContext} from './ChangeBuilds.context'
import ChangeBuildsGrid from './ChangeBuildsGrid'
import ChangeBuildType from './ChangeBuildType/ChangeBuildType'
import ChangeBuildTypeDetails from './ChangeBuildTypeDetails/ChangeBuildTypeDetails'

import styles from './ChangeBuilds.css'

type Props = {
  buildTypes: ReadonlyArray<BuildType>
  buildsHash: KeyValue<BuildTypeId, NormalizedBuild[]>
  isDeployment: boolean | undefined
}

export function ChangeBuildsFlatList({buildTypes, buildsHash, isDeployment}: Props) {
  const {withInfiniteScroll, pageSize} = useContext(ChangeBuildsContext)

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [showAll, setShowAll] = useState<boolean>(false)

  const trancatedBuildTypes = useMemo(
    () => (showAll ? buildTypes : buildTypes.slice(0, currentPage * pageSize)),
    [buildTypes, currentPage, pageSize, showAll],
  )

  const showMoreData = useCallback(() => {
    setCurrentPage(currentPage + 1)
  }, [currentPage])

  const showAllData = useCallback(() => {
    setShowAll(true)
  }, [])

  const hasMore = buildTypes.length !== trancatedBuildTypes.length

  const renderContent = useMemo(
    () => (
      <ChangeBuildsGrid>
        {trancatedBuildTypes.map(({id}) => {
          if (id == null) {
            return null
          }
          const buildIds = buildsHash[id]?.map(build => build.id) ?? emptyArray
          return (
            <div className={styles.row} key={stringifyId(id)}>
              <ChangeBuildType buildIds={buildIds} buildTypeId={id} isDeployment={isDeployment} />
              <ChangeBuildTypeDetails buildIds={buildIds} />
            </div>
          )
        })}
      </ChangeBuildsGrid>
    ),
    [buildsHash, isDeployment, trancatedBuildTypes],
  )

  return (
    <div>
      {withInfiniteScroll ? (
        <InfiniteScroll
          className={styles.infiniteScroll}
          dataLength={trancatedBuildTypes.length}
          hasMore={hasMore}
          next={showMoreData}
          loader={<div className={styles.loader}>{'Loading...'}</div>}
        >
          {renderContent}
        </InfiniteScroll>
      ) : (
        <>
          {renderContent}
          {hasMore ? (
            <div className={styles.loadMoreButtonWrapper}>
              <Button onClick={showMoreData}>{'Load more'}</Button>
              {currentPage > 1 ? (
                <Button className={styles.showAllButton} onClick={showAllData} text>
                  {'show all'}
                </Button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}
